"use client";
import React, { useState, useRef, useEffect, ReactNode } from "react";
import {
  AppCropImage,
  AppFormControlLayoutPanel,
  AppFormControlTextField,
  AppFromLabelField,
  AppModal,
  AppTextField,
  AppUploadFileActions,
} from "@/common";
import { ReactCropperElement, ReactCropperProps } from "react-cropper";
import {
  Box,
  Button,
  FormHelperText,
  InputLabelProps,
  Stack,
  SxProps,
} from "@mui/material";

import { CategoryTypeUploadEnum, IUploadFile } from "@/models";
import { AppFormControlLayoutPanelProps } from "@/common/form-control/AppFormControlLayoutPanel";
import { useDropzone } from "react-dropzone";
import { useHandleUploadImage } from "@/hooks";
import { AppConstant, DataConstant } from "@/const";
import { CommonUtils } from "@/utils";
import AppButton from "../../AppButton";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldError,
  RegisterOptions,
} from "react-hook-form";
import { AppUploadFileActionsProps } from "@/common/AppUploadFileActions";

const CoverImageControl = ({
  label = "Ảnh",
  classNameContainer,
  labelProps,
  control,
  name,
  heightCoverImg = 274,
  imgSrc,
  onUploadThumb,
  onClearImageCover,
  hasClearIcon = true,
  imageSx,
  cropImageProps,
  hasButtonAction = true,
  imageRatio = AppConstant.IMAGE_RATIO,
  rules,
  helper,
  ownerId = "",
  categoryType,
  uploadFileActionsProps,
  ...otherProps
}: CoverImageControlProps) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const handleUploadImage = useHandleUploadImage();

  const [isUploading, setIsUploading] = useState(false);
  const [isOpenModalPreview, setIsOpenModalPreview] = useState(false);
  const [image, setImage] = useState("");
  const [imageFile, setImageFile] = useState<File>();

  const handleDropZone = (acceptedFiles: Array<File>) => {
    if (Array.isArray(acceptedFiles) && acceptedFiles.length) {
      setIsOpenModalPreview(true);
      setImageFile(acceptedFiles[0]);
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result as any);
      };
      reader.readAsDataURL(acceptedFiles[0]);
    }
  };

  const { open } = useDropzone({
    multiple: false,
    accept: AppConstant.ACCEPT_IMAGE,
    onDrop: handleDropZone,
  });

  const handleCloseModal = () => {
    setImageFile(undefined);
    setImage("");
    setIsOpenModalPreview(false);
  };

  const handleUploadFile = async () => {
    setIsUploading(true);
    if (typeof cropperRef.current?.cropper !== "undefined" && imageFile) {
      const url = cropperRef.current?.cropper.getCroppedCanvas().toDataURL();
      const file = CommonUtils.convertBase64ToFile(url, imageFile.name);
      const fileArr = await handleUploadImage([file], categoryType, ownerId);

      if (fileArr.length) {
        handleCloseModal();
        onUploadThumb(fileArr);
      }
    }
    setIsUploading(false);
  };
  const [isError, setIsError] = useState(false);
  return (
    <>
      <AppFromLabelField
        label={label}
        labelProps={{
          ...labelProps,
        }}
        className={classNameContainer}
        required={labelProps?.required}
        helper={helper}
      >
        <Box className="flex items-center flex-col">
          <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field, fieldState: { error: errorField } }) => {
              return (
                <>
                  <AppUploadFileActions
                    sx={{
                      // height: heightCoverImg,
                      // width: imageRatio * heightCoverImg,
                      width: "100%",
                      aspectRatio: imageRatio,
                      padding: "1px",
                      ...imageSx,
                    }}
                    dropzoneOptions={{
                      multiple: false,
                      accept: AppConstant.ACCEPT_IMAGE,
                      onDrop: handleDropZone,
                    }}
                    url={imgSrc}
                    onClearFile={onClearImageCover}
                    hasClearIcon={hasClearIcon}
                    isError={isError}
                    {...uploadFileActionsProps}
                  />
                  <TextFieldRender
                    field={field}
                    errorField={errorField}
                    setIsError={setIsError}
                  />
                  {hasButtonAction && (
                    <Box className="flex gap-2 justify-center mt-3">
                      <AppButton
                        variant="contained"
                        color="primary"
                        className="min-w-[90px]"
                        onClick={open}
                      >
                        Thay đổi
                      </AppButton>
                      <AppButton
                        variant="contained"
                        color="primary"
                        className="min-w-[90px]"
                        onClick={onClearImageCover}
                      >
                        Xóa
                      </AppButton>
                    </Box>
                  )}
                </>
              );
            }}
          />
        </Box>
      </AppFromLabelField>
      <AppModal
        fullScreen
        open={isOpenModalPreview}
        onClose={handleCloseModal}
        PaperProps={{
          sx: { "&&&": { width: 600, maxWidth: "unset", paddingTop: "40px" } },
        }}
        content={
          <Box>
            <AppCropImage ref={cropperRef} src={image} {...cropImageProps} />
          </Box>
        }
        footer={
          <AppButton
            disabled={isUploading}
            variant="contained"
            onClick={handleUploadFile}
            color="primary"
          >
            {isUploading ? "Đang tải lên ..." : "Tải lên"}
          </AppButton>
        }
      />
    </>
  );
};

export type CoverImageControlProps = {
  control: Control;
  rules?: RegisterOptions;
  name: string;
  heightCoverImg?: number;
  imgSrc: string;
  onUploadThumb: (file: Array<IUploadFile>) => void;
  onClearImageCover: () => void;
  imageSx?: SxProps;
  cropImageProps?: ReactCropperProps;
  hasClearIcon?: boolean;
  hasButtonAction?: boolean;
  imageRatio?: number;
  label?: string | ReactNode;
  classNameContainer?: string;
  labelProps?: InputLabelProps;
  helper?: string | ReactNode;
  categoryType: CategoryTypeUploadEnum;
  ownerId?: string;
  uploadFileActionsProps?: Pick<
    AppUploadFileActionsProps,
    "title" | "description"
  >;
};

export default CoverImageControl;

const TextFieldRender = ({
  field,
  errorField,
  setIsError,
}: {
  field: ControllerRenderProps<any, string>;
  errorField?: FieldError;
  setIsError: (value: React.SetStateAction<boolean>) => void;
}) => {
  useEffect(() => {
    setIsError(!!errorField);
  }, [errorField]);

  return (
    <>
      <AppTextField className="hidden" {...field} error={!!errorField} />
      {errorField && (
        <FormHelperText className="text-error mt-0 mb-1">
          {errorField?.message}
        </FormHelperText>
      )}
    </>
  );
};
